/**
 * Обёртка над `DocsAPI.DocEditor` (Сервер документов Р7).
 *
 * WUI-316: `onDocumentStateChange` → `setSessionWasDirty`; `onRequestClose` → закрытие модалки
 * в `ElementRenderer` (не `onEditorClosed` напрямую). `forcesave: true` для кнопки «Сохранить».
 *
 * @see dev/WUI-305.md, dev/WUI-316.md §4.2
 */
import type { R7DocEditorInitConfig } from '@/common/types/r7DocEditorInitConfig'
import { onBeforeUnmount, ref, shallowRef } from 'vue'
import { getR7DocumentServerApiScriptUrl } from '@/common/constants/r7DocumentServer'
import {
  isR7ModalEditorHost,
  resolveR7EditorIframeHeightPx,
} from '@/common/utils/resolveR7EditorIframeHeight'
import { invokeR7DocEditorForceSave } from '@/common/utils/r7DocEditorForceSave'

export type R7DocumentType = 'word' | 'cell' | 'slide' | 'pdf'

export type R7EditorInitOptions = {
  /** Полный подписываемый init-config от middleware (`office-editor/prepare`). */
  docEditorInitConfig: R7DocEditorInitConfig
  /** Browser JWT (корневое поле DocEditor config, не часть payload). */
  token?: string
  onEditorEvent?: (name: string, payload: unknown) => void
}

type DocEditorInstance = {
  destroyEditor?: () => void
  serviceCommand?: (command: string, data?: unknown) => void
  createConnector?: () => {
    executeMethod?: (method: string, data?: unknown, callback?: (result: unknown) => void) => void
  } | null
}

type DocsApiGlobal = {
  DocEditor: new (
    placeholderId: string,
    config: Record<string, unknown>,
  ) => DocEditorInstance
}

declare global {
  interface Window {
    DocsAPI?: DocsApiGlobal
  }
}

let scriptLoadPromise: Promise<void> | null = null

/** Однократная подгрузка `{DS}/web-apps/apps/api/documents/api.js` в `window.DocsAPI`. */
function loadR7ApiScript(): Promise<void> {
  if (window.DocsAPI?.DocEditor) {
    return Promise.resolve()
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const src = getR7DocumentServerApiScriptUrl()
    const existing = document.querySelector<HTMLScriptElement>(`script[data-r7-docs-api="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error(`Не удалось загрузить ${src}`)), {
        once: true,
      })
      return
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = src
    script.async = true
    script.dataset.r7DocsApi = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Не удалось загрузить скрипт Р7: ${src}`))
    document.head.appendChild(script)
  })

  return scriptLoadPromise
}

const EDITOR_HOST_MIN_HEIGHT_PX = 200

const EDITOR_HOST_MIN_WIDTH_PX = 400

/** DS считает height при init; в модальном окне до @show контейнер часто 0px — видна только панель. */
async function waitForEditorHostLayout(placeholderId: string, maxAttempts = 40): Promise<HTMLElement> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve())
    })
    const el = document.getElementById(placeholderId)
    if (
      el &&
      el.offsetHeight >= EDITOR_HOST_MIN_HEIGHT_PX &&
      el.offsetWidth >= EDITOR_HOST_MIN_WIDTH_PX
    ) {
      return el
    }
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 50)
    })
  }
  const el = document.getElementById(placeholderId)
  if (!el) {
    throw new Error(`Контейнер редактора #${placeholderId} не найден`)
  }
  return el
}

/**
 * Height — явные px (иначе DS открывает только toolbar).
 * Width — 100%, чтобы iframe тянулся за диалогом, а не залипал на замере при init.
 * Inline: ⅔ ширины, не ниже 480px; модалка — высота host.
 */
function resolveEditorDimensions(host: HTMLElement): { width: string; height: string } {
  const heightPx = resolveR7EditorIframeHeightPx(host)
  if (!isR7ModalEditorHost(host)) {
    const wrap = host.closest('.r7-inline-host') as HTMLElement | null
    if (wrap) wrap.style.height = `${heightPx}px`
    host.style.height = `${heightPx}px`
  }
  return {
    width: '100%',
    height: `${heightPx}px`,
  }
}

/**
 * Composable редактора Р7 для одного DOM-контейнера (`#placeholderId`).
 * Единственное место вызова `new DocsAPI.DocEditor(...)`.
 */
export function useR7DocEditor(placeholderId: string) {
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const editorInstance = shallowRef<DocEditorInstance | null>(null)

  /** Уничтожает iframe DS и сбрасывает ссылку на экземпляр. */
  const destroyEditor = () => {
    try {
      editorInstance.value?.destroyEditor?.()
    } catch {
      /* ignore teardown errors */
    }
    editorInstance.value = null
  }

  /** DocsAPI save while iframe is alive. `true` only if a command actually ran. */
  const requestForceSave = (): boolean => invokeR7DocEditorForceSave(editorInstance.value)

  /**
   * Создаёт `new DocsAPI.DocEditor(placeholderId, config)`.
   * Подписываемый config приходит с middleware; webui добавляет только layout/events.
   */
  const initEditor = async (options: R7EditorInitOptions) => {
    error.value = null
    isLoading.value = true
    destroyEditor()

    try {
      await loadR7ApiScript()
      const DocEditor = window.DocsAPI?.DocEditor
      if (!DocEditor) {
        throw new Error('DocsAPI.DocEditor не найден после загрузки api.js')
      }

      const host = await waitForEditorHostLayout(placeholderId)
      if (!host.isConnected) {
        throw new Error(`Контейнер редактора #${placeholderId} удалён из DOM`)
      }
      const { width, height } = resolveEditorDimensions(host)

      const rawInitConfig = options.docEditorInitConfig
      if (!rawInitConfig?.document?.key || !rawInitConfig.document.url) {
        throw new Error('Ответ prepare не содержит docEditorInitConfig для инициализации Р7.')
      }
      const docEditorInitConfig = JSON.parse(
        JSON.stringify(rawInitConfig),
      ) as R7DocEditorInitConfig

      const config: Record<string, unknown> = {
        document: docEditorInitConfig.document,
        documentType: docEditorInitConfig.documentType,
        editorConfig: docEditorInitConfig.editorConfig,
        ...(options.token ? { token: options.token } : {}),
        height,
        width,
        events: {
          onAppReady: () => options.onEditorEvent?.('onAppReady', null),
          onDocumentReady: () => options.onEditorEvent?.('onDocumentReady', null),
          onError: (event: { data?: unknown }) => {
            console.error('[R7 DocEditor] onError', event)
            options.onEditorEvent?.('onError', event?.data ?? event)
          },
          onWarning: (event: { data?: unknown }) => {
            console.warn('[R7 DocEditor] onWarning', event)
            options.onEditorEvent?.('onWarning', event?.data ?? event)
          },
          /** WUI-316: `true` = пользователь изменил документ (`wasDirty`). */
          onDocumentStateChange: (event: { data?: boolean }) => {
            options.onEditorEvent?.('onDocumentStateChange', event.data === true)
          },
          onRequestClose: () => options.onEditorEvent?.('onRequestClose', null),
        },
      }

      editorInstance.value = new DocEditor(placeholderId, config)
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      error.value = message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  onBeforeUnmount(() => {
    destroyEditor()
  })

  return {
    isLoading,
    error,
    initEditor,
    destroyEditor,
    requestForceSave,
  }
}
